import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Button, Card, Flex, Form, message, Space, Upload} from "antd";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import {useParams} from "react-router-dom";
import Title from "antd/es/typography/Title";
import Stages from "../../components/brief/Stages";
import type {GetProp, UploadProps} from 'antd';
import IconDelete from "../../components/ui/icons/IconDelete";
import IconUploadFile from "../../components/ui/icons/IconUploadFile";
import {Model, useModels} from "../../hooks/useModels";
import Roles from "../../roles/Roles";
import useSWR from "swr";
import getBriefParamsString from "../../api/params";
import "../../assets/documents.scss";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const uploadFile = async (formData: any) => {
  return await api.postFile(`${urls.mediaObjects}`, {arg: formData})
};

interface Brief {
  setupValidation: boolean,
  isPresentationRequired: boolean,
  isCalculatorRequired: boolean,
}

export default function Documents() {
  const {t} = useTranslation();
  const routeParams = useParams();
  const briefId = Number(routeParams.id) || 0;
  const {models, getModels, deleteFileFromModel, addFileToModel} = useModels(briefId);
  const [modelsForm] = Form.useForm();
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const [brief, setBrief] = useState<Brief>({
    setupValidation: false,
    isPresentationRequired: false,
    isCalculatorRequired: false,
  });

  const {
    data: briefData,
    mutate: mutateBrief,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (briefData && briefData.data) {
      setBrief({
        setupValidation: briefData.data.setupValidation,
        isPresentationRequired: briefData.data.isPresentationRequired,
        isCalculatorRequired: briefData.data.isCalculatorRequired,
      })
    }
  }, [briefData]);

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  };

  useEffect(() => {
    if (models && models.length > 0) {
      modelsForm.setFieldValue('models', models)
    }
  }, [models]);

  const onSave = (values: any) => {
    if (!Roles.hasAccess('canUpdateFilesInDocuments')) {
      return
    }

    setIsLoading(true);

    Promise.all([...values.models.filter((model: Model) => model.fileBlob && model.fileBlob?.length > 0).map((model: Model) => {
        const formData = new FormData();
        model.fileBlob?.forEach((file) => {
          formData.append('file', file.originFileObj as FileType)
        });

        return uploadFile(formData)
        .then(response => {
          return addFileToModel(model.id, response['@id'])
        }).catch((response) => {
          return Promise.reject(response['hydra:description'])
        })
      }
    )]).then(values => {
      message.success(t('common.saved'));
      getModels();
      setIsValuesChanged(false)
    })
    .catch((error) => {
      message.error(error)
    })
    .finally(() => {
      setIsLoading(false)
    })
  };

  const removeFileFromModel = (modelId: number, index: number) => {
    deleteFileFromModel(modelId).then(response => {
      message.success(t('document.fileDeleted'));
      getModels();
      modelsForm.setFieldValue(["models", index, "fileBlob"], null)
    })
  };

  const isModelUploadDisabled = (sid: string) => {
    switch (sid) {
      case 'validation':
        return !brief.setupValidation;

      case 'presentation':
        return !brief.isPresentationRequired;

      case 'calculator':
        return !brief.isCalculatorRequired;
    }

    return true
  };

  return (
    <Flex gap={'large'} justify={'flex-end'}>
      <Space direction={'vertical'} size={'middle'} style={{width: '640px'}}>
        {models &&
          <Flex vertical gap={'middle'}>
            <Form
              form={modelsForm}
              name="models"
              onFinish={onSave}
              layout="vertical"
              autoComplete="off"
              onValuesChange={() => {
                setIsValuesChanged(true)
              }}
            >
              <Flex vertical gap={'middle'}>
                <Form.List name="models">
                  {(fields, {add, remove}) => (
                    <Flex vertical gap={'middle'}>
                      {fields.map(({key, name, ...restField}) => (
                        <Card key={name}>
                          <Flex vertical gap={'middle'}>
                            <Title level={5}>{modelsForm.getFieldValue(["models", name, "fileTypeName"])}</Title>
                            {modelsForm.getFieldValue(["models", name, "file"]) &&
                              <Flex className={'file__item'} gap={'small'} align={'center'} justify={'space-between'}>
                                <Button
                                  color="default"
                                  variant="link"
                                  disabled={!Roles.hasAccess('canDownloadFilesInDocuments')}
                                  onClick={() => {
                                    api.getFile(`${process.env.REACT_APP_PUBLIC_ENTRYPOINT}${modelsForm.getFieldValue(["models", name, "fileUrl"])}`)
                                  }}
                                >
                                  <Flex justify={'space-between'} align={'center'}>
                                    <Flex gap={'small'}>
                                      <div className={'file__item-preview'}>
                                        <Flex className={'file__item-type'}>
                                          {modelsForm.getFieldValue(["models", name, "type"]) || 'File'}
                                        </Flex>
                                      </div>
                                      <div className={'file__item-info'}>
                                        <Flex gap={2} vertical>
                                          <div
                                            className={'file__item-name'}>{modelsForm.getFieldValue(["models", name, "fileName"]) || t('dataset.dataset')}</div>
                                        </Flex>
                                      </div>
                                    </Flex>
                                  </Flex>
                                </Button>
                                {Roles.hasAccess('canUpdateFilesInDocuments') && !isLoading &&
                                  <div className={'delete-button'} onClick={(event) => {
                                    event.preventDefault();
                                    removeFileFromModel(modelsForm.getFieldValue(["models", name, "id"]), name)
                                  }}>
                                    <IconDelete/>
                                  </div>
                                }
                              </Flex>
                            }
                            <Form.Item
                              {...restField}
                              name={[name, 'fileBlob']}
                              valuePropName="fileList"
                              getValueFromEvent={normFile}
                            >
                              <Upload
                                name="logo"
                                listType="picture"
                                beforeUpload={(file) => {
                                  return false
                                }}
                                style={{width: '100%'}}
                                disabled={
                                  !Roles.hasAccess('canUpdateFilesInDocuments')
                                  || isModelUploadDisabled(modelsForm.getFieldValue(["models", name, "fileTypeSid"]))
                                  || isLoading
                                }
                              >
                                <Button icon={<IconUploadFile/>} size={'large'} disabled={isLoading}
                                        style={{width: '100%'}}>{t('dataset.uploadFile')}</Button>
                              </Upload>
                            </Form.Item>
                          </Flex>
                        </Card>
                      ))}
                    </Flex>
                  )}
                </Form.List>
              </Flex>
            </Form>
          </Flex>
        }

        <ButtonBlack
          onClick={() => {
            modelsForm.submit()
          }}
          loading={isLoading}
          disabled={!isValuesChanged}
        >{t('common.save')}</ButtonBlack>
      </Space>
      <Card style={{width: '636px'}}>
        <Flex vertical gap={16}>
          <Title level={5}>{t('edit.stages')}</Title>

          <Flex vertical gap={4}>
            <Stages />
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
